import type {
  ChannelLocationStatusDto,
  ReputationOverviewDto,
} from '@up2stay/up2promo-api-client';

import { locationsNeedsAttentionHref } from '@/core/components/locations/locations-list-filter-url';
import { LOCATION_ATTENTION_RATING_THRESHOLD } from '@/core/components/reputation/reputation-thresholds';
import { formatReputationScoreValue } from '@/core/components/reputation/reputation-score-formula';
import {
  GOOGLE_REVIEW_PROVIDER,
  isLocationLinked,
} from '@/core/lib/review-channel-setup';
import { shellPaths } from '@/core/router/app-paths';

import {
  HEALTH_ROW_LIMIT,
  mergeRankedLocations,
} from './dashboard-status-helpers';
import type {
  DashboardHealthRow,
  DashboardHealthStatus,
  DashboardStatusLabels,
} from './dashboard-status-types';

export function buildDashboardHealthRows(input: {
  reputationEnabled: boolean;
  overview: ReputationOverviewDto | undefined;
  locations: { id: string; name?: string | null; address?: string | null }[];
  failedSyncLocationIds: Set<string>;
  /** Google channel link status — used to avoid false “Connect Google” CTAs. */
  channelLocations?: ChannelLocationStatusDto[];
  labels: DashboardStatusLabels;
}): DashboardHealthRow[] {
  const {
    reputationEnabled,
    overview,
    locations,
    failedSyncLocationIds,
    channelLocations = [],
    labels,
  } = input;
  const rows: DashboardHealthRow[] = [];

  if (reputationEnabled && overview) {
    const critical = overview.unrepliedCriticalCount ?? 0;
    const withoutReviews = overview.locationsWithoutReviews ?? [];
    const workspaceStatus: DashboardHealthStatus =
      critical > 0
        ? 'critical'
        : withoutReviews.length > 0
          ? 'warning'
          : overview.averageRating > 0 &&
              overview.averageRating < LOCATION_ATTENTION_RATING_THRESHOLD
            ? 'warning'
            : overview.linkedLocationCount === 0
              ? 'setup'
              : 'ok';

    rows.push({
      id: 'workspace',
      name: labels.workspaceHealth,
      status: workspaceStatus,
      rating: overview.totalReviews > 0 ? overview.averageRating : undefined,
      scoreLabel:
        overview.totalReviews > 0
          ? formatReputationScoreValue(overview.score)
          : undefined,
      meta:
        critical > 0
          ? labels.unrepliedCritical.replace('{count}', String(critical))
          : withoutReviews.length > 0
            ? labels.zeroReviewsMeta.replace(
                '{count}',
                String(withoutReviews.length),
              )
            : labels.linkedLocations.replace(
                '{count}',
                String(overview.linkedLocationCount),
              ),
      href:
        critical > 0
          ? shellPaths.reputationInbox
          : withoutReviews.length === 1
            ? shellPaths.moreWorkspaceLocationDetail(
                withoutReviews[0]!.locationId,
              )
            : shellPaths.reputationDashboard,
      needsGoogleConnect: workspaceStatus === 'setup',
    });

    const ranked = mergeRankedLocations(
      overview.locationRanking.top,
      overview.locationRanking.bottom,
    );
    const rankedIds = new Set(ranked.map((loc) => loc.locationId));
    const visibilityGaps = withoutReviews.filter(
      (loc) => !rankedIds.has(loc.locationId),
    );

    const attentionFirst = [
      ...visibilityGaps,
      ...ranked.sort((a, b) => a.averageRating - b.averageRating),
    ];

    for (const loc of attentionFirst.slice(0, HEALTH_ROW_LIMIT - 1)) {
      rows.push(
        healthRowForLocation(
          loc,
          failedSyncLocationIds,
          channelLocations,
          labels,
        ),
      );
    }
  } else if (!reputationEnabled) {
    for (const loc of locations.slice(0, HEALTH_ROW_LIMIT)) {
      rows.push({
        id: loc.id,
        name: loc.name ?? loc.id,
        status: 'unknown',
        // The row's own address, not the workspace total (DEV-536).
        meta: loc.address ?? undefined,
        href: shellPaths.moreWorkspaceLocationDetail(loc.id),
        needsGoogleConnect: false,
      });
    }
  }

  return rows;
}

function healthRowForLocation(
  loc: {
    locationId: string;
    locationName: string;
    averageRating: number;
    totalReviews: number;
  },
  failedSyncLocationIds: Set<string>,
  channelLocations: ChannelLocationStatusDto[],
  labels: DashboardStatusLabels,
): DashboardHealthRow {
  const syncFailed = failedSyncLocationIds.has(loc.locationId);
  const noReviews = loc.totalReviews === 0;
  const googleLinked =
    channelLocations.length === 0
      ? // Overview-sourced rows are linked by definition; avoid false connect CTA
        // while channel roster is still empty/unavailable.
        true
      : isLocationLinked(
          channelLocations,
          loc.locationId,
          GOOGLE_REVIEW_PROVIDER,
        );
  const status: DashboardHealthStatus = syncFailed
    ? 'sync'
    : !googleLinked
      ? 'setup'
      : noReviews || loc.averageRating < LOCATION_ATTENTION_RATING_THRESHOLD
        ? 'warning'
        : 'ok';
  return {
    id: loc.locationId,
    name: loc.locationName,
    status,
    rating: noReviews || !googleLinked ? undefined : loc.averageRating,
    meta: syncFailed
      ? labels.syncFailed
      : !googleLinked
        ? labels.setupConnectGoogle
        : noReviews
          ? labels.zeroReviews
          : labels.reviewsCount.replace('{count}', String(loc.totalReviews)),
    href: syncFailed
      ? locationsNeedsAttentionHref(shellPaths.moreWorkspaceLocations)
      : !googleLinked
        ? shellPaths.reputationSetup
        : noReviews
          ? shellPaths.moreWorkspaceLocationDetail(loc.locationId)
          : `${shellPaths.reputationInbox}?${new URLSearchParams({
              locationId: loc.locationId,
            }).toString()}`,
    needsGoogleConnect: !googleLinked && !syncFailed,
  };
}
